import {
  AVAILABLE_USE_CASES,
  hasRequiredLibrary,
  type LineOfBusiness,
  type RunCardTemplateKind,
  type UseCaseSpec,
  type WorkMoment,
  type WorkloadTask
} from './use-case-catalog';

export const SUGGESTION_COUNT = 6;
export const MAX_TASKS = 3;

/**
 * The most suggestions one preset family may contribute.
 *
 * Siblings in a family share their tags, so they score identically and sort
 * together. Someone who says they classify risks and work new business matches all
 * three Classify entries at the same score, and without a cap half the suggestions
 * are one errand with a different code set. Two shows the family has range and
 * leaves room for the rest of the week.
 */
export const MAX_PER_FAMILY = 2;

/** `mixed` is a real answer: nothing is filtered out on the line question. */
export type BookAnswer = LineOfBusiness | 'mixed';

export type SuggestionAnswers = {
  book?: BookAnswer;
  tasks: WorkloadTask[];
  moments: WorkMoment[];
};

export const EMPTY_ANSWERS: SuggestionAnswers = { tasks: [], moments: [] };

const TASK_WEIGHT = 3;
const MOMENT_WEIGHT = 2;
const BOOK_WEIGHT = 1;

export const servesBook = (useCase: UseCaseSpec, book: BookAnswer | undefined) =>
  !book || book === 'mixed' || useCase.lines.includes(book);

export const suggestionScore = (useCase: UseCaseSpec, answers: SuggestionAnswers): number => {
  const tasks = answers.tasks.filter((task) => useCase.tasks.includes(task)).length;
  const moments = answers.moments.filter((moment) => useCase.moments.includes(moment)).length;
  const book =
    answers.book && answers.book !== 'mixed' && useCase.lines.includes(answers.book) ? 1 : 0;
  return tasks * TASK_WEIGHT + moments * MOMENT_WEIGHT + book * BOOK_WEIGHT;
};

/** Best-first, capped at `SUGGESTION_COUNT`. Drops unrunnable or locked presets. Ties keep catalog order. */
export const suggestedUseCases = (
  answers: SuggestionAnswers,
  context: {
    libraries: Partial<Record<RunCardTemplateKind, { length: number }>>;
    hasProAccess: boolean;
  }
): UseCaseSpec[] => {
  const ranked = AVAILABLE_USE_CASES.filter(
    (useCase) =>
      hasRequiredLibrary(useCase, context.libraries) &&
      (!useCase.pro || context.hasProAccess) &&
      servesBook(useCase, answers.book) &&
      suggestionScore(useCase, answers) > 0
  )
    .map((useCase, index) => ({ useCase, index, score: suggestionScore(useCase, answers) }))
    .sort((a, b) => b.score - a.score || a.index - b.index);

  // Taken in rank order, so the two a family contributes are its two best. A family
  // already at its cap is skipped, and the slot goes to the next preset down.
  const takenPerFamily = new Map<UseCaseSpec['preset'], number>();
  const picked: UseCaseSpec[] = [];
  for (const { useCase } of ranked) {
    if (picked.length === SUGGESTION_COUNT) {
      break;
    }
    const taken = takenPerFamily.get(useCase.preset) ?? 0;
    if (taken === MAX_PER_FAMILY) {
      continue;
    }
    takenPerFamily.set(useCase.preset, taken + 1);
    picked.push(useCase);
  }
  return picked;
};
